import { useHandler } from "@redotech/react-util/hook";
import { BackInStockInsertionMode } from "@redotech/redo-marketing-settings-common/back-in-stock-insertion-mode";
import { WidgetAutocheck } from "@redotech/redo-storefront-widget-common/widget";
import { filterTruthy } from "@redotech/util/array";
import { useCallback, useEffect, useRef, useState } from "react";
import { CONCIERGE_PRODUCT_FORM_CONTAINER_ID } from "../../../concierge/pre-init";
import { log } from "../../../monitoring/log";
import {
  getAllPdpAddProductForms,
  getSoldOutFallbackElements,
} from "../../../product-page";
import { isOnProductPage } from "../../../shopify";
import { getRenderSafeDomObserver } from "../../../utils/dom";
import { RedoBackInStockContainerElement } from "./element";

let nextElementId = 0;

const bisInsertionFunctions: Record<
  BackInStockInsertionMode,
  (form: Element, element: RedoBackInStockContainerElement) => void
> = {
  [BackInStockInsertionMode.APPEND_CHILD]: (form, element) =>
    form.append(element),
  [BackInStockInsertionMode.INSERT_BEFORE]: (form, element) =>
    form.before(element),
  [BackInStockInsertionMode.INSERT_AFTER]: (form, element) =>
    form.after(element),
};

interface RedoBackInStockPlacement {
  element: RedoBackInStockContainerElement;
  target: Element;
}

export function useBackInStockRoot(
  autocheck: WidgetAutocheck,
): RedoBackInStockPlacement[] {
  const [placements, setPlacements_] = useState<RedoBackInStockPlacement[]>([]);
  const placementsRef = useRef<RedoBackInStockPlacement[]>([]);
  const setPlacements = useCallback(
    (placements: RedoBackInStockPlacement[]) => {
      setPlacements_(placements);
      placementsRef.current = placements;
    },
    [setPlacements_],
  );

  const discoverAndAttachForms = useHandler(() => {
    const onTapcart = !!globalThis.Tapcart && !globalThis.Shopify;
    const hasConciergeContainer = !!document.querySelector(
      CONCIERGE_PRODUCT_FORM_CONTAINER_ID,
    );

    if (!isOnProductPage(onTapcart) && !hasConciergeContainer) {
      if (placementsRef.current.length > 0) {
        setPlacements([]);
      }
      return;
    }

    let targets = getAllPdpAddProductForms({
      onTapcart,
      autocheckSelector: autocheck.marketing.backInStockButtonSelector,
    });
    let usingFallback = false;

    if (targets.length === 0 && autocheck.marketing.backInStockButtonSelector) {
      targets = getAllPdpAddProductForms({ onTapcart });
    }

    if (targets.length === 0) {
      targets = getSoldOutFallbackElements();
      usingFallback = true;

      if (targets.length === 0) {
        if (placementsRef.current.length > 0) {
          setPlacements([]);
        }
        return;
      }
    }

    // Prune elements that are no longer in the DOM
    const surviving = placementsRef.current.filter(
      (el) => el.element.isConnected,
    );

    // Build a set of target elements that already have our custom element
    const occupiedTargets = new Set<Element>(
      filterTruthy(surviving.map((placement) => placement.target)),
    );

    // Find targets that don't already contain one of our elements
    const newTargets = targets.filter((form) => !occupiedTargets.has(form));

    if (
      newTargets.length === 0 &&
      surviving.length === placementsRef.current.length
    ) {
      // Nothing changed
      return;
    }

    RedoBackInStockContainerElement.register();

    const created: RedoBackInStockPlacement[] = [];
    for (const form of newTargets) {
      const el = document.createElement(
        RedoBackInStockContainerElement.TAG,
      ) as RedoBackInStockContainerElement;
      el.id = `redo-back-in-stock-container-${nextElementId++}`;
      const mode =
        autocheck.marketing.backInStockInsertionMode ??
        BackInStockInsertionMode.APPEND_CHILD;
      bisInsertionFunctions[mode](form, el);
      created.push({ element: el, target: form });
    }

    if (created.length > 0) {
      log(
        `back-in-stock: embedding in ${created.length} ${usingFallback ? "fallback " : ""}forms`,
      );
    }

    const updated = [...surviving, ...created];
    setPlacements(updated);
  });

  useEffect(() => {
    discoverAndAttachForms();

    const domObserver = getRenderSafeDomObserver();
    domObserver.registerCallback(discoverAndAttachForms);

    return () => {
      domObserver.unregisterCallback(discoverAndAttachForms);
    };
  }, [discoverAndAttachForms]);

  return placements;
}
